import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, Users, Clock, Send, MessageCircle, ChevronDown, MapPin, Camera, ArrowUpDown, Search, X, Loader2 } from 'lucide-react';
import { getFeed, getLocationSuggestions } from '../api/photos';
import { updateLocation } from '../api/location';
import { saveLocationToHistory } from '../services/attributeMatcher';
import { getPhotoMessages, sendMessage, getMessageCount } from '../api/messages';

// Sorting options for Messages tab
const SORT_OPTIONS = {
  RECENT: 'recent',
  LOCATION: 'location',
  PHOTOGRAPHER: 'photographer',
};

function Feed({ user, onFeedViewed }) {
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoMessages, setPhotoMessages] = useState({});
  const [newMessages, setNewMessages] = useState({});
  const [sendingMessage, setSendingMessage] = useState({});
  const [activeTab, setActiveTab] = useState('im-in-it');
  const [expandedChats, setExpandedChats] = useState({});
  const [expandedShares, setExpandedShares] = useState({});
  const [messagesSortBy, setMessagesSortBy] = useState(SORT_OPTIONS.RECENT);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationSearch, setLocationSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Close suggestions and sort menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSortMenu && !e.target.closest('[data-testid="sort-dropdown-btn"]') && !e.target.closest('[data-testid="sort-dropdown-menu"]')) {
        setShowSortMenu(false);
      }
      if (showSuggestions && suggestionsRef.current && !suggestionsRef.current.contains(e.target) && !e.target.closest('[data-testid="location-search-input"]')) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSortMenu]);

  useEffect(() => {
    loadFeed(null);
    updateUserLocation();
    
    // Mark notifications as seen when viewing feed
    // Use immediate call to ensure it's triggered
    const markSeen = async () => {
      if (onFeedViewed) {
        await onFeedViewed();
      }
    };
    markSeen();
    
    // Also mark as seen when component unmounts (user leaves feed)
    // This handles edge cases where user switches tabs quickly
    return () => {
      if (onFeedViewed) {
        onFeedViewed();
      }
    };
  }, []);

  const updateUserLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        updateLocation(latitude, longitude);
        saveLocationToHistory(latitude, longitude);
        setUserLocation({ latitude, longitude });
      },
      (error) => {
      }
    );
  };

  const loadFeed = useCallback(async (searchTerm = null) => {
    setIsSearching(true);
    try {
      const data = await getFeed(0, 100, searchTerm);
      setFeed(data.items);
      // Load messages for all photos that can have chat
      loadAllMessages(data.items);
    } catch (error) {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload feed when location search changes
  useEffect(() => {
    if (!loading) {
      loadFeed(locationSearch || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch, loadFeed]);

  // Fetch suggestions with debounce
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      const data = await getLocationSuggestions(query);
      setSuggestions(data.suggestions || []);
      setShowSuggestions((data.suggestions || []).length > 0);
    } catch (error) {
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  // Handle search input change with debounce
  const handleSearchInputChange = (value) => {
    setSearchInput(value);
    
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounce suggestion fetch
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSearch = () => {
    setLocationSearch(searchInput);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (location) => {
    setSearchInput(location);
    setLocationSearch(location);
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    setSearchInput('');
    setLocationSearch('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const loadAllMessages = async (items) => {
    const messages = {};
    for (const item of items) {
      if ((item.is_owner && item.recipients_count > 0) || item.shared_with_me) {
        try {
          const data = await getPhotoMessages(item.id);
          messages[item.id] = data.messages || [];
        } catch (error) {
          messages[item.id] = [];
        }
      }
    }
    setPhotoMessages(messages);
  };

  const loadMessagesForPhoto = async (photoId) => {
    try {
      const data = await getPhotoMessages(photoId);
      setPhotoMessages(prev => ({ ...prev, [photoId]: data.messages || [] }));
    } catch (error) {
    }
  };

  const handleSendMessage = async (photoId) => {
    const message = newMessages[photoId]?.trim();
    if (!message) return;

    setSendingMessage(prev => ({ ...prev, [photoId]: true }));
    try {
      await sendMessage(photoId, message);
      setNewMessages(prev => ({ ...prev, [photoId]: '' }));
      await loadMessagesForPhoto(photoId);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(prev => ({ ...prev, [photoId]: false }));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    return date.toLocaleDateString();
  };

  const toggleExpandChat = (photoId) => {
    setExpandedChats(prev => ({ ...prev, [photoId]: !prev[photoId] }));
  };

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
    
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Sort messages feed based on selected option
  const sortMessagesFeed = (items) => {
    if (messagesSortBy === SORT_OPTIONS.RECENT) {
      return [...items].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    
    if (messagesSortBy === SORT_OPTIONS.LOCATION) {
      if (!userLocation) {
        return items; // Can't sort by location without user location
      }
      return [...items].sort((a, b) => {
        const distA = a.location 
          ? calculateDistance(userLocation.latitude, userLocation.longitude, a.location.latitude, a.location.longitude)
          : Infinity;
        const distB = b.location 
          ? calculateDistance(userLocation.latitude, userLocation.longitude, b.location.latitude, b.location.longitude)
          : Infinity;
        return distA - distB;
      });
    }
    
    if (messagesSortBy === SORT_OPTIONS.PHOTOGRAPHER) {
      return [...items].sort((a, b) => {
        const nameA = a.owner_name || '';
        const nameB = b.owner_name || '';
        return nameA.localeCompare(nameB);
      });
    }
    
    return items;
  };

  // Filter photos based on active tab
  const getFilteredFeed = () => {
    switch (activeTab) {
      case 'im-in-it':
        return feed.filter(item => item.shared_with_me);
      case 'i-took-it':
        return feed.filter(item => item.is_owner);
      case 'messages':
        const messagesItems = feed.filter(item => (photoMessages[item.id]?.length || 0) > 0);
        return sortMessagesFeed(messagesItems);
      default:
        return feed;
    }
  };

  const filteredFeed = getFilteredFeed();

  // Group messages by photographer (for display)
  const groupedByPhotographer = useMemo(() => {
    if (activeTab !== 'messages' || messagesSortBy !== SORT_OPTIONS.PHOTOGRAPHER) {
      return null;
    }
    
    const groups = {};
    filteredFeed.forEach(item => {
      const photographer = item.owner_name || 'Unknown';
      if (!groups[photographer]) {
        groups[photographer] = [];
      }
      groups[photographer].push(item);
    });
    return groups;
  }, [filteredFeed, activeTab, messagesSortBy]);

  // Tab configuration
  const tabs = [
    { id: 'im-in-it', label: "I'm In It", description: 'Photos of me' },
    { id: 'i-took-it', label: 'I Took It', description: 'My photos' },
    { id: 'messages', label: 'Messages', description: 'Conversations' },
  ];

  // Get counts for each tab
  const tabCounts = {
    'im-in-it': feed.filter(item => item.shared_with_me).length,
    'i-took-it': feed.filter(item => item.is_owner).length,
    'messages': feed.filter(item => (photoMessages[item.id]?.length || 0) > 0).length,
  };

  // Sort option labels
  const sortLabels = {
    [SORT_OPTIONS.RECENT]: 'Most Recent',
    [SORT_OPTIONS.LOCATION]: 'Nearest to Me',
    [SORT_OPTIONS.PHOTOGRAPHER]: 'By Photographer',
  };

  const canChat = (item) => (item.is_owner && item.recipients_count > 0) || item.shared_with_me;

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: '0', paddingBottom: '80px' }}>
      {/* Header */}
      <div style={{ 
        padding: '1rem 1rem 0.5rem', 
        background: 'hsl(var(--background))',
        borderBottom: '1px solid hsl(var(--border))'
      }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '0.25rem', fontWeight: 600 }}>Feed</h1>
        <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
          Your photos and shared memories
        </p>

        {/* Location Search */}
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem',
            alignItems: 'center',
          }}>
            <div style={{ 
              flex: 1,
              position: 'relative',
            }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <MapPin 
                  size={16} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.75rem', 
                    color: 'hsl(var(--muted-foreground))',
                    zIndex: 1,
                  }} 
                />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                    if (e.key === 'Escape') {
                      setShowSuggestions(false);
                    }
                  }}
                  onFocus={() => {
                    if (suggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  placeholder="Search by city, state, or zip code..."
                  style={{
                    width: '100%',
                    padding: '0.5rem 2rem 0.5rem 2.25rem',
                    borderRadius: 'var(--radius)',
                    border: '1px solid hsl(var(--border))',
                    background: 'hsl(var(--muted))',
                    fontSize: '0.8rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  data-testid="location-search-input"
                />
                {(searchInput || loadingSuggestions) && (
                  <button
                    onClick={clearSearch}
                    disabled={loadingSuggestions}
                    style={{
                      position: 'absolute',
                      right: '0.5rem',
                      padding: '0.25rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'hsl(var(--muted-foreground))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    data-testid="clear-search-btn"
                  >
                    {loadingSuggestions ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                  </button>
                )}
              </div>

              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.25rem',
                    background: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 'var(--radius)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    zIndex: 50,
                    maxHeight: '240px',
                    overflowY: 'auto',
                  }}
                  data-testid="suggestions-dropdown"
                >
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={suggestion.location}
                      onClick={() => handleSuggestionClick(suggestion.location)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.625rem 0.75rem',
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        color: 'hsl(var(--foreground))',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => e.target.style.background = 'hsl(var(--muted))'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      data-testid={`suggestion-${index}`}
                    >
                      <MapPin size={14} style={{ color: 'hsl(var(--primary))', flexShrink: 0 }} />
                      <span style={{ 
                        flex: 1, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                      }}>
                        {suggestion.location}
                      </span>
                      <span style={{
                        fontSize: '0.7rem',
                        color: 'hsl(var(--muted-foreground))',
                        background: 'hsl(var(--muted))',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '9999px',
                        flexShrink: 0,
                      }}>
                        {suggestion.photo_count} {suggestion.photo_count === 1 ? 'photo' : 'photos'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleSearch}
              disabled={isSearching}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: 'var(--radius)',
                border: 'none',
                background: 'hsl(var(--primary))',
                color: 'white',
                fontSize: '0.8rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                opacity: isSearching ? 0.7 : 1,
              }}
              data-testid="search-btn"
            >
              <Search size={14} />
              Search
            </button>
          </div>
          
          {/* Active search indicator */}
          {locationSearch && (
            <div style={{
              marginTop: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              fontSize: '0.75rem',
              color: 'hsl(var(--muted-foreground))',
            }}>
              <span>Showing results for:</span>
              <span style={{
                background: 'hsl(var(--primary) / 0.1)',
                color: 'hsl(var(--primary))',
                padding: '0.125rem 0.5rem',
                borderRadius: '9999px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}>
                <MapPin size={10} />
                {locationSearch}
              </span>
              <button
                onClick={clearSearch}
                style={{
                  padding: '0.125rem 0.375rem',
                  background: 'hsl(var(--muted))',
                  border: 'none',
                  borderRadius: '9999px',
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                  color: 'hsl(var(--muted-foreground))',
                }}
                data-testid="clear-filter-btn"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div 
          style={{ 
            display: 'flex', 
            gap: '0.25rem',
            background: 'hsl(var(--muted))',
            padding: '0.25rem',
            borderRadius: 'calc(var(--radius) + 2px)'
          }}
          data-testid="feed-tabs"
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const count = tabCounts[tab.id];
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.125rem',
                  padding: '0.5rem 0.25rem',
                  borderRadius: 'var(--radius)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background: isActive ? 'hsl(var(--background))' : 'transparent',
                  boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  color: isActive ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                }}
                data-testid={`tab-${tab.id}`}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: isActive ? 600 : 400 }}>
                    {tab.label}
                  </span>
                  {count > 0 && (
                    <span style={{
                      background: isActive ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground) / 0.3)',
                      color: isActive ? 'white' : 'hsl(var(--muted-foreground))',
                      fontSize: '0.625rem',
                      fontWeight: 600,
                      padding: '0.0625rem 0.3rem',
                      borderRadius: '9999px',
                      minWidth: '16px',
                      textAlign: 'center'
                    }}>
                      {count}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Sort options for Messages tab */}
        {activeTab === 'messages' && (
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              marginTop: '0.75rem',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.375rem 0.75rem',
                borderRadius: 'var(--radius)',
                border: '1px solid hsl(var(--border))',
                background: 'hsl(var(--background))',
                color: 'hsl(var(--foreground))',
                fontSize: '0.75rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
              data-testid="sort-dropdown-btn"
            >
              <ArrowUpDown style={{ width: '14px', height: '14px' }} />
              <span>{sortLabels[messagesSortBy]}</span>
              <ChevronDown 
                style={{ 
                  width: '14px', 
                  height: '14px',
                  transform: showSortMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }} 
              />
            </button>

            {/* Sort dropdown menu */}
            {showSortMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '0.25rem',
                  background: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 'var(--radius)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 50,
                  minWidth: '160px',
                  overflow: 'hidden',
                }}
                data-testid="sort-dropdown-menu"
              >
                <button
                  onClick={() => { setMessagesSortBy(SORT_OPTIONS.RECENT); setShowSortMenu(false); }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.625rem 0.875rem',
                    border: 'none',
                    background: messagesSortBy === SORT_OPTIONS.RECENT ? 'hsl(var(--muted))' : 'transparent',
                    color: 'hsl(var(--foreground))',
                    fontSize: '0.8125rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                  data-testid="sort-recent"
                >
                  <Clock style={{ width: '16px', height: '16px', color: 'hsl(var(--muted-foreground))' }} />
                  Most Recent
                </button>
                <button
                  onClick={() => { setMessagesSortBy(SORT_OPTIONS.LOCATION); setShowSortMenu(false); }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.625rem 0.875rem',
                    border: 'none',
                    background: messagesSortBy === SORT_OPTIONS.LOCATION ? 'hsl(var(--muted))' : 'transparent',
                    color: userLocation ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                    fontSize: '0.8125rem',
                    textAlign: 'left',
                    cursor: userLocation ? 'pointer' : 'not-allowed',
                    opacity: userLocation ? 1 : 0.6,
                  }}
                  disabled={!userLocation}
                  data-testid="sort-location"
                >
                  <MapPin style={{ width: '16px', height: '16px', color: 'hsl(var(--muted-foreground))' }} />
                  Nearest to Me
                  {!userLocation && <span style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>(needs location)</span>}
                </button>
                <button
                  onClick={() => { setMessagesSortBy(SORT_OPTIONS.PHOTOGRAPHER); setShowSortMenu(false); }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.625rem 0.875rem',
                    border: 'none',
                    background: messagesSortBy === SORT_OPTIONS.PHOTOGRAPHER ? 'hsl(var(--muted))' : 'transparent',
                    color: 'hsl(var(--foreground))',
                    fontSize: '0.8125rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                  data-testid="sort-photographer"
                >
                  <Camera style={{ width: '16px', height: '16px', color: 'hsl(var(--muted-foreground))' }} />
                  By Photographer
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="container" style={{ padding: '1rem' }}>
        {filteredFeed.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
            {locationSearch ? (
              <>
                <MapPin size={32} style={{ margin: '0 auto 0.75rem', color: 'hsl(var(--muted-foreground))' }} />
                <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem', fontWeight: 500 }}>
                  No photos found in "{locationSearch}"
                </p>
                <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  Try searching for a different city, state, or zip code.
                </p>
                <button
                  onClick={clearSearch}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: 'var(--radius)',
                    border: '1px solid hsl(var(--border))',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: 'hsl(var(--primary))',
                  }}
                  data-testid="clear-search-empty-btn"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                {activeTab === 'im-in-it' && (
                  <>
                    <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem', fontWeight: 500 }}>
                      No photos of you yet
                    </p>
                    <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem' }}>
                      When someone takes a photo that includes you, it will appear here.
                    </p>
                  </>
                )}
                {activeTab === 'i-took-it' && (
                  <>
                    <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem', fontWeight: 500 }}>
                      No photos taken yet
                    </p>
                    <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem' }}>
                      Use the camera to take your first photo!
                    </p>
                  </>
                )}
                {activeTab === 'messages' && (
                  <>
                    <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem', fontWeight: 500 }}>
                      No conversations yet
                    </p>
                    <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem' }}>
                      Start chatting on a shared photo to see it here.
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {filteredFeed.map((item) => {
              const messages = photoMessages[item.id] || [];
              const isExpanded = expandedChats[item.id];
              const visibleMessages = isExpanded ? messages : messages.slice(-6);  /* Show last 6 messages by default */
              const hasMoreMessages = messages.length > 6;

              return (
                <div key={item.id} className="card" style={{ overflow: 'hidden', padding: 0 }} data-testid="feed-item">
                  {/* Photo with overlaid messages and watermark */}
                  <div style={{ position: 'relative' }}>
                    <img
                      src={item.photo_url}
                      alt="Photo"
                      onClick={() => navigate(`/photo/${item.id}`)}
                      style={{ 
                        width: '100%', 
                        display: 'block',
                        minHeight: '250px',
                        maxHeight: '400px',
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                      data-testid={`photo-image-${item.id}`}
                    />
                    
                    {/* Messages overlay - RIGHT SIDE - FULL HEIGHT */}
                    {messages.length > 0 && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: '2.5rem',  /* Below the top info bar */
                          bottom: '2.5rem', /* Leave room for watermark */
                          right: '0.5rem',
                          left: '35%',  /* Only take right 65% of photo */
                          overflowY: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',  /* Messages start from bottom */
                          gap: '0.375rem',
                          padding: '0.5rem 0',
                          zIndex: 5
                        }}
                        data-testid="messages-overlay"
                      >
                        {/* Show more/less button - only if there are many messages */}
                        {hasMoreMessages && (
                          <button
                            onClick={() => toggleExpandChat(item.id)}
                            style={{
                              alignSelf: 'flex-end',
                              background: 'rgba(0,0,0,0.5)',
                              border: 'none',
                              borderRadius: '9999px',
                              padding: '0.2rem 0.5rem',
                              color: 'white',
                              fontSize: '0.6rem',
                              cursor: 'pointer',
                              backdropFilter: 'blur(4px)',
                              marginBottom: '0.25rem'
                            }}
                          >
                            {isExpanded ? 'Show less' : `+${messages.length - 6} more`}
                          </button>
                        )}

                        {/* Messages */}
                        {visibleMessages.map((msg) => {
                          const isOwn = msg.user_id === user?.user_id;
                          return (
                            <div 
                              key={msg.message_id}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',  /* All messages align right */
                              }}
                            >
                              <div
                                style={{
                                  background: isOwn ? 'hsl(var(--primary))' : 'rgba(255,255,255,0.9)',
                                  color: isOwn ? 'white' : '#1a1a1a',
                                  padding: '0.375rem 0.625rem',
                                  borderRadius: '1rem',
                                  maxWidth: '85%',
                                  fontSize: '0.8rem',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                }}
                              >
                                {!isOwn && (
                                  <span style={{ 
                                    fontSize: '0.65rem', 
                                    fontWeight: 600, 
                                    display: 'block',
                                    marginBottom: '0.125rem',
                                    opacity: 0.8
                                  }}>
                                    {msg.user_name}
                                  </span>
                                )}
                                <span>{msg.content}</span>
                              </div>
                              <span style={{ 
                                fontSize: '0.6rem', 
                                color: 'rgba(255,255,255,0.7)',
                                marginTop: '0.125rem',
                                paddingLeft: isOwn ? 0 : '0.5rem',
                                paddingRight: isOwn ? '0.5rem' : 0
                              }}>
                                {formatMessageTime(msg.created_at)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Watermark - bottom left corner - ALWAYS SHOW */}
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '0.5rem',
                        left: '0.5rem',
                        zIndex: 20
                      }}
                      data-testid={`watermark-${item.id}`}
                    >
                      {item.watermark?.url && item.watermark.url.length > 0 ? (
                        <a
                          href={item.watermark.url.startsWith('http') ? item.watermark.url : `https://${item.watermark.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.375rem 0.625rem',
                            background: 'rgba(0,0,0,0.7)',
                            backdropFilter: 'blur(4px)',
                            borderRadius: '6px',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textDecoration: 'none',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          }}
                        >
                          <span>📷</span>
                          {item.watermark?.text || item.owner_name}
                        </a>
                      ) : (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.375rem 0.625rem',
                            background: 'rgba(0,0,0,0.7)',
                            backdropFilter: 'blur(4px)',
                            borderRadius: '6px',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          }}
                        >
                          <span>📷</span>
                          {item.watermark?.text || item.owner_name}
                        </span>
                      )}
                    </div>

                    {/* Photo info overlay (top) */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(rgba(0,0,0,0.5), transparent)',
                      padding: '0.75rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.375rem',
                      zIndex: 15
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <User size={14} color="white" />
                          <span style={{ color: 'white', fontWeight: 500, fontSize: '0.8rem' }}>
                            {item.is_owner ? 'You' : item.owner_name}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                          <Clock size={12} />
                          {formatDate(item.created_at)}
                        </div>
                      </div>
                      {/* Location display */}
                      {item.location?.name && (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.375rem',
                          color: 'rgba(255,255,255,0.85)',
                          fontSize: '0.7rem',
                        }}>
                          <MapPin size={11} />
                          <span style={{ 
                            maxWidth: '80%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {item.location.name}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Badges */}
                    <div style={{
                      position: 'absolute',
                      top: item.location?.name ? '3.5rem' : '2.5rem',
                      left: '0.75rem',
                      display: 'flex',
                      gap: '0.375rem',
                      flexWrap: 'wrap',
                      zIndex: 15
                    }}>
                      {item.shared_with_me && (
                        <span style={{
                          background: 'rgba(22, 163, 74, 0.9)',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.65rem',
                          fontWeight: 500
                        }}>
                          Shared with you
                        </span>
                      )}
                      {item.is_owner && item.recipients_count > 0 && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedShares(prev => ({
                              ...prev,
                              [item.id]: !prev[item.id]
                            }));
                          }}
                          data-testid={`shared-with-badge-${item.id}`}
                          style={{
                            background: 'rgba(59, 130, 246, 0.9)',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '9999px',
                            fontSize: '0.65rem',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            cursor: 'pointer',
                            transition: 'background-color 200ms ease',
                          }}
                        >
                          <Users size={10} />
                          Shared with {item.recipients_count}
                          <ChevronDown size={10} style={{
                            transform: expandedShares[item.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 200ms ease',
                          }} />
                        </span>
                      )}
                      {messages.length > 0 && (
                        <span style={{
                          background: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.65rem',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          <MessageCircle size={10} />
                          {messages.length}
                        </span>
                      )}
                    </div>

                    {/* Expandable shared-with names */}
                    {item.is_owner && expandedShares[item.id] && item.shared_with && item.shared_with.length > 0 && (
                      <div
                        data-testid={`shared-names-${item.id}`}
                        style={{
                          position: 'absolute',
                          top: '5rem',
                          left: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.75)',
                          backdropFilter: 'blur(12px)',
                          borderRadius: '0.75rem',
                          padding: '0.5rem 0.75rem',
                          zIndex: 18,
                          maxWidth: '60%',
                        }}
                      >
                        <div style={{
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          color: 'rgba(255,255,255,0.6)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '0.375rem',
                        }}>
                          Shared with
                        </div>
                        {item.shared_with.map((person) => (
                          <div
                            key={person.user_id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              padding: '0.25rem 0',
                            }}
                          >
                            <div style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              background: 'hsl(188, 82%, 35%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              <User size={10} color="white" />
                            </div>
                            <span style={{
                              fontSize: '0.75rem',
                              color: 'white',
                              fontWeight: 500,
                            }}>
                              {person.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Message input - only for photos that can have chat */}
                  {canChat(item) && (
                    <div style={{
                      padding: '0.75rem',
                      borderTop: '1px solid hsl(var(--border))',
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'center',
                      background: 'hsl(var(--background))'
                    }}>
                      <input
                        type="text"
                        value={newMessages[item.id] || ''}
                        onChange={(e) => setNewMessages(prev => ({ ...prev, [item.id]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(item.id);
                          }
                        }}
                        placeholder="Say something..."
                        maxLength={500}
                        style={{
                          flex: 1,
                          padding: '0.5rem 0.75rem',
                          borderRadius: '9999px',
                          border: '1px solid hsl(var(--border))',
                          background: 'hsl(var(--muted))',
                          fontSize: '0.85rem',
                          outline: 'none'
                        }}
                        data-testid={`message-input-${item.id}`}
                      />
                      <button
                        onClick={() => handleSendMessage(item.id)}
                        disabled={!newMessages[item.id]?.trim() || sendingMessage[item.id]}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '50%',
                          border: 'none',
                          background: newMessages[item.id]?.trim() ? 'hsl(var(--primary))' : 'hsl(var(--muted))',
                          color: newMessages[item.id]?.trim() ? 'white' : 'hsl(var(--muted-foreground))',
                          cursor: newMessages[item.id]?.trim() ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s'
                        }}
                        data-testid={`send-button-${item.id}`}
                      >
                        <Send size={18} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Feed;
